import * as React from "react";
import { Button, Field, Input, makeStyles } from "@fluentui/react-components";
import { DATAVERSE_OPERATOR_SPECS } from "@kodikas/fetchxml-ts";
import type { ConditionDraft, QueryEntityMetadata } from "../../../types.js";
import { FilteredComboBox } from "../../shared/FilteredCombobox.js";
import { valueEditorMode } from "../fetchXmlBuilderHelpers.js";

const useStyles = makeStyles({
  conditionBox: {
    border: "1px solid var(--colorNeutralStroke2)",
    borderRadius: "6px",
    padding: "8px",
    display: "grid",
    gap: "8px",
  },
  row: {
    display: "flex",
    gap: "8px",
    alignItems: "center",
    flexWrap: "wrap",
  },
});

export interface ConditionEditorProps {
  condition: ConditionDraft;
  targetMetadata: QueryEntityMetadata | null;
  onChangeCondition: (conditionId: string, update: Partial<ConditionDraft>) => void;
  onRemoveCondition: (conditionId: string) => void;
}

export function ConditionEditor({ condition, targetMetadata, onChangeCondition, onRemoveCondition }: ConditionEditorProps): JSX.Element {
  const styles = useStyles();
  const arity = valueEditorMode(condition);

  return (
    <div className={styles.conditionBox}>
      <div className={styles.row}>
        <Field label="Attribute" style={{ minWidth: 280 }}>
          <FilteredComboBox
            options={(targetMetadata?.attributes || []).map((attr) => [attr.logicalName, `${attr.displayName} (${attr.logicalName})`] as [string, string])}
            selectedOption={condition.attribute}
            onValueChange={(value) => onChangeCondition(condition.id, { attribute: value })}
          />
        </Field>

        <Field label="Operator" style={{ minWidth: 240 }}>
          <FilteredComboBox
            options={DATAVERSE_OPERATOR_SPECS.map((item) => [item.operator, `${item.operator} (${item.arity})`] as [string, string])}
            selectedOption={condition.operator}
            onValueChange={(value) =>
              onChangeCondition(condition.id, {
                operator: value,
                value: "",
                secondValue: "",
                multiValues: "",
                valueOfAttribute: "",
              })
            }
          />
        </Field>

        <Button appearance="subtle" onClick={() => onRemoveCondition(condition.id)}>
          Remove condition
        </Button>
      </div>

      <Field label="Value of (optional, for column-to-column comparisons)" style={{ minWidth: 340 }}>
        <Input value={condition.valueOfAttribute} onChange={(_, data) => onChangeCondition(condition.id, { valueOfAttribute: data.value })} />
      </Field>

      {condition.valueOfAttribute.trim().length === 0 && arity === "single" ? (
        <Field label="Value">
          <Input value={condition.value} onChange={(_, data) => onChangeCondition(condition.id, { value: data.value })} />
        </Field>
      ) : null}

      {condition.valueOfAttribute.trim().length === 0 && arity === "multiple" ? (
        <Field label="Values (comma separated)">
          <Input value={condition.multiValues} onChange={(_, data) => onChangeCondition(condition.id, { multiValues: data.value })} />
        </Field>
      ) : null}

      {condition.valueOfAttribute.trim().length === 0 && arity === "exactly-two" ? (
        <div className={styles.row}>
          <Field label="First value">
            <Input value={condition.value} onChange={(_, data) => onChangeCondition(condition.id, { value: data.value })} />
          </Field>
          <Field label="Second value">
            <Input value={condition.secondValue} onChange={(_, data) => onChangeCondition(condition.id, { secondValue: data.value })} />
          </Field>
        </div>
      ) : null}
    </div>
  );
}
